/**
 * RecordLookupMenu — MENU TRA CỨU quanh MỘT bản ghi, dựng bằng bộ MỚI (DataListManager / ReportFrame).
 *
 * Bù khoảng trống §6.5 của docs/UI-MIGRATION-INPUTBASE-TO-DATALISTMANAGER.md: nút "..." trên form của
 * InputBase (MainForm.jsx:400-476) có sổ cái / sổ kho / công nợ phải thu / công nợ phải trả / lịch sử
 * cập nhật / dữ liệu đã xoá / đổi mã, còn bộ mới trước đây không có mục nào.
 *
 * Khác bộ cũ ở chỗ: bộ cũ mở các mục này qua `objectPicker`/`quickReport` → SysForms → InputBase/ReportBase.
 * Ở đây dựng thẳng bằng DataListManager/ReportFrame nên KHÔNG kéo bộ cũ vào bundle. Ba màn hình tra cứu
 * (`data_socai`, `data_sokho`, `log_deleted`) chỉ tồn tại dưới dạng `listinfo`, nên cột được dịch tại chỗ
 * bằng `adaptListinfoFields`.
 */
import React, { useState, useContext, useMemo, useCallback, Suspense, lazy } from 'react';
import {
  Box, Typography, Button, IconButton, MenuItem, Divider,
  CircularProgress, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useQuery } from '@tanstack/react-query';

import AuthContext from './auth/Context';
import Dialog from './ManagedModal';
// Menu/Tooltip PHẢI đi qua ManagedDropdown: chúng được mở từ bên trong form (ManagedModal), mà form đã
// được ngăn xếp z-index cấp một số CAO HƠN 1300. Menu của MUI mặc định đứng ở 1300 nên rơi xuống DƯỚI
// form — nhìn như không bấm được. ManagedDropdown xin số từ CHÍNH ngăn xếp đó nên luôn nổi lên trên.
import ManagedDropdown from './ManagedDropdown';
import { adaptListinfoFields } from './adaptListinfoFields';
import { chayHeaderScript } from './listinfoHeaderScript';

// NẠP TRỄ ba component nặng này. Hai lý do:
//  1) CẮT VÒNG LẶP IMPORT: menu được gắn vào chính DataView/DataListForm, mà ở đây lại cần
//     DataListManager/DataListForm để dựng hộp thoại tra cứu.
//  2) Không kéo chúng vào chunk đầu — menu chỉ mở khi người dùng thật sự bấm.
const DataListManager = lazy(() => import('./DataListManager'));
const ReportFrame = lazy(() => import('./ReportFrame'));
const DataListForm = lazy(() => import('./DataListForm'));

const DangTai = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
);

// Mã màn hình tra cứu của hệ thống (đều là bản ghi `listinfo`).
const CODE_SOCAI = 'data_socai';
const CODE_SOKHO = 'data_sokho';
const CODE_DELETED = 'log_deleted';
// Hai báo cáo công nợ theo chứng từ (đã có sẵn trong core).
const RPT_PHAI_THU = 'chitietthutientheohoadon';
const RPT_PHAI_TRA = 'chitietchitientheohoadon';

// =====================================================================
// Hộp thoại DANH SÁCH tra cứu — DataListManager chỉ-đọc, cột dịch từ listinfo
// =====================================================================
function LookupListDialog({ open, onClose, title, listinfoCode, condition }) {
  const context = useContext(AuthContext);
  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');

  const { data: cfg, isLoading, isError, error } = useQuery({
    queryKey: ['lookup_listinfo_cfg', listinfoCode],
    queryFn: async () => {
      const li = await context.apis.asyncGetData(userToken, 'listinfo', { code: listinfoCode }, null, true, true);
      if (!li) throw new Error(`Chưa có màn hình '${listinfoCode}' trên hệ thống này.`);
      return {
        apiCode: li.api_code || li.code,
        title: li.title || listinfoCode,
        // `header` dạng script (nhãn lấy theo cấu hình công ty) phải chạy trước khi dịch cột.
        fields: adaptListinfoFields(await chayHeaderScript(li.fields || [], li, context), { onlyDisplay: true }),
      };
    },
    enabled: !!open && !!listinfoCode && !!userToken,
    staleTime: 10 * 60 * 1000,
  });

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3, height: '85vh' } }}>
      <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight="bold">{title || cfg?.title || listinfoCode}</Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
        ) : isError || !cfg ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">{error?.message || `Không mở được màn hình '${listinfoCode}'.`}</Typography>
          </Box>
        ) : (
          <Suspense fallback={<DangTai />}>
          <DataListManager
            key={`lookup_${listinfoCode}`}
            code={listinfoCode}
            apiCode={cfg.apiCode}
            title={title || cfg.title}
            fields={cfg.fields}
            condition={condition}
            canAdd={false} canEdit={false} canDelete={false} canCopy={false}
            hideHeader
            containerSx={{ borderRadius: 0, height: '100%' }}
          />
          </Suspense>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained" disableElevation>Đóng</Button>
      </DialogActions>
    </Dialog>
  );
}

// =====================================================================
// Hộp thoại BÁO CÁO tra cứu — ReportFrame (không dùng ReportBase của bộ cũ)
// =====================================================================
function LookupReportDialog({ open, onClose, title, reportCode, condition }) {
  const context = useContext(AuthContext);
  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');

  const { data: cfg, isLoading, isError, error } = useQuery({
    queryKey: ['lookup_report_cfg', reportCode],
    queryFn: async () => {
      // Ưu tiên model MỚI `reportconfig` (đã đúng định dạng), thiếu thì lấy `reportinfo` cũ rồi dịch.
      try {
        const res = await context.apis.asyncGetList(userToken, 'reportconfig', { condition: { code: reportCode }, limit: 1 });
        const item = res?.data?.[0] || res?.[0];
        if (item) return item.toObject ? item.toObject() : (item._doc || item);
      } catch (e) { /* hệ thống chưa có reportconfig -> rơi xuống reportinfo */ }

      const res = await context.apis.asyncGetList(userToken, 'reportinfo', { condition: { code: reportCode }, limit: 1 });
      let item = res?.data?.[0] || res?.[0];
      if (!item) throw new Error(`Chưa có báo cáo '${reportCode}' trên hệ thống này.`);
      if (item.columns && !item.displayFields) {
        // import ĐỘNG để tránh vòng lặp import (DataDynamicPage -> DataListManager -> ... -> file này).
        const { adaptLegacyReportConfig } = await import('./DataDynamicPage');
        item = await adaptLegacyReportConfig(item, context);
      }
      return item;
    },
    enabled: !!open && !!reportCode && !!userToken,
    staleTime: 10 * 60 * 1000,
  });

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3, height: '85vh' } }}>
      <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight="bold">{title}</Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
        ) : isError || !cfg ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">{error?.message || `Không mở được báo cáo '${reportCode}'.`}</Typography>
          </Box>
        ) : (
          <Suspense fallback={<DangTai />}>
            <ReportFrame key={`lookup_rpt_${reportCode}`} {...cfg} title={title} containerSx={{ borderRadius: 0 }} defaultCondition={condition} />
          </Suspense>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained" disableElevation>Đóng</Button>
      </DialogActions>
    </Dialog>
  );
}

// =====================================================================
// Hộp thoại LỊCH SỬ CẬP NHẬT — đọc /log của bản ghi
// =====================================================================
// Field do hệ thống ghi, hiện ra chỉ làm nhiễu (bộ cũ cũng lọc đúng nhóm này).
const LOG_NOISE_FIELDS = ['is_full_data', 'collection_name', 'date_updated', 'user_updated', 'approve_data', 'ten_trang_thai', 'color'];

function UpdateLogDialog({ open, onClose, apiCode, recordId }) {
  const context = useContext(AuthContext);
  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');

  const { data: logs, isLoading, isError, error } = useQuery({
    queryKey: ['record_update_log', apiCode, recordId],
    queryFn: async () => {
      const rows = await context.apis.asyncGetLogById(userToken, apiCode, recordId);
      return (rows || []).map((log) => {
        const changed = { ...(log.data_changed || {}) };
        for (const k of LOG_NOISE_FIELDS) delete changed[k];
        return {
          user: log.name_user_created || log.user_created,
          email: log.user_created,
          date: log.date_created,
          action: log.action,
          ip: (log.ip || '').split(':').pop(),
          changed,
        };
      });
    },
    enabled: !!open && !!apiCode && !!recordId && !!userToken,
    staleTime: 60 * 1000,
  });

  const fmtDate = (d) => {
    try { return new Date(d).toLocaleString('vi-VN'); } catch (e) { return String(d || ''); }
  };

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight="bold">Lịch sử cập nhật</Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
        ) : isError ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="error">{error?.message || 'Không đọc được lịch sử.'}</Typography></Box>
        ) : !logs || logs.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">Bản ghi này chưa có lần sửa nào.</Typography></Box>
        ) : (
          <TableContainer sx={{ maxHeight: '65vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Thời điểm</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Người thực hiện</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Thao tác</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nội dung thay đổi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((l, i) => {
                  const keys = Object.keys(l.changed || {});
                  return (
                    <TableRow key={i}>
                      <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>{fmtDate(l.date)}</TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Typography variant="body2">{l.user || '—'}</Typography>
                        {l.ip && <Typography variant="caption" color="text.secondary">{l.ip}</Typography>}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Chip size="small" label={l.action || '—'} sx={{ height: 20, fontSize: '0.7rem' }} />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        {keys.length === 0 ? (
                          <Typography variant="caption" color="text.secondary" fontStyle="italic">(không có thay đổi dữ liệu)</Typography>
                        ) : (
                          <Box component="pre" sx={{ m: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {JSON.stringify(l.changed, null, 1)}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained" disableElevation>Đóng</Button>
      </DialogActions>
    </Dialog>
  );
}

// =====================================================================
// ĐỔI MÃ — gọi endpoint updatekey (có cascade ở server)
// =====================================================================
function ChangeCodeDialog({ open, onClose, apiCode, item, formCode, onChanged }) {
  const context = useContext(AuthContext);
  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');

  const { data: fields } = useQuery({
    queryKey: ['change_code_form', formCode],
    queryFn: async () => {
      const li = await context.apis.asyncGetData(userToken, 'listinfo', { code: formCode }, null, true, true);
      return adaptListinfoFields(await chayHeaderScript((li && li.fields) || [], li, context));
    },
    enabled: !!open && !!formCode && !!userToken,
    staleTime: 10 * 60 * 1000,
  });

  const handleSave = useCallback(async (formData) => {
    const entries = Object.entries(formData || {}).filter(([k, v]) => k !== '_id' && v !== undefined && v !== null && v !== '');
    if (entries.length === 0) { context.alert('Chưa nhập mã mới.', null, 'orange'); return; }
    const [field, newValue] = entries[0];
    const idApp = item.id_app || context.config?.id_app || '';
    const url = `${context.config.server_url}/api/${idApp}/${apiCode}/updatekey/${item._id}/${field}/${encodeURIComponent(newValue)}?access_token=${userToken}`;
    context.setProgressStatus(true);
    try {
      await context.apis.asyncGet(url);
      context.alert('Đã đổi mã xong.', null, 'green');
      onClose();
      if (onChanged) onChanged({ field, value: newValue });
    } catch (e) {
      context.alert(e?.message || e?.error || String(e), null, 'red');
    } finally {
      context.setProgressStatus(false);
    }
  }, [apiCode, item, context, userToken, onClose, onChanged]);

  if (!open) return null;
  return (
    <Suspense fallback={null}>
      <DataListForm
        open={open}
        onClose={onClose}
        isEditing={false}
        initialData={{}}
        fields={fields || []}
        onSave={handleSave}
        formSize="sm"
      />
    </Suspense>
  );
}

// =====================================================================
// MENU CHÍNH
// =====================================================================
export default function RecordLookupMenu({
  item,          // bản ghi đang xem/sửa
  apiCode,       // mã API thật (dùng cho /log và /updatekey)
  changeCodeForm, // mã form đổi mã (options.change_code_form của listinfo); không có -> ẩn mục Đổi mã
  onChanged,     // gọi lại sau khi đổi mã thành công (để màn hình cha tải lại)
  size = 'small',
}) {
  const context = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialog, setDialog] = useState(null);   // 'socai' | 'sokho' | 'phaithu' | 'phaitra' | 'log' | 'deleted' | 'doima'

  const recordId = item && (item._id || item.id);
  // Chứng từ mới có sổ sách/công nợ (bộ cũ xét isVoucher + có ma_ct; ở đây `ma_ct` là dấu hiệu đủ chắc).
  const isVoucherRecord = !!(item && item.ma_ct);

  const deletedCondition = useMemo(() => {
    const cond = { action: 'DELETE', id_func: apiCode };
    const notRequire = (context.apis && context.apis.list_not_require_id_app) || [];
    if (notRequire.indexOf(apiCode) < 0) cond.id_app = context.config?.id_app || '';
    return cond;
  }, [apiCode, context]);

  if (!recordId || !apiCode) return null;

  const close = () => setAnchorEl(null);
  const open = (name) => { setDialog(name); setAnchorEl(null); };
  const label = (text) => (context.apis && context.apis.getLabel) ? context.apis.getLabel(text) : text;

  return (
    <>
      <ManagedDropdown type="tooltip" title="Tra cứu">
        <IconButton size={size} onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize={size} />
        </IconButton>
      </ManagedDropdown>

      <ManagedDropdown type="menu" anchorEl={anchorEl} open={!!anchorEl} onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {isVoucherRecord && <MenuItem onClick={() => open('socai')}>{label('Dữ liệu sổ cái')}</MenuItem>}
        {isVoucherRecord && <MenuItem onClick={() => open('sokho')}>{label('Dữ liệu sổ kho')}</MenuItem>}
        {isVoucherRecord && <MenuItem onClick={() => open('phaithu')}>{label('Công nợ phải thu')}</MenuItem>}
        {isVoucherRecord && <MenuItem onClick={() => open('phaitra')}>{label('Công nợ phải trả')}</MenuItem>}
        {isVoucherRecord && <Divider />}
        <MenuItem onClick={() => open('log')}>{label('Lịch sử cập nhật')}</MenuItem>
        <MenuItem onClick={() => open('deleted')}>{label('Dữ liệu đã xoá')}</MenuItem>
        {changeCodeForm && <Divider />}
        {changeCodeForm && <MenuItem onClick={() => open('doima')}>{label('Đổi mã')}</MenuItem>}
      </ManagedDropdown>

      <LookupListDialog open={dialog === 'socai'} onClose={() => setDialog(null)}
        title={label('Dữ liệu sổ cái')} listinfoCode={CODE_SOCAI} condition={{ id_ct: recordId }} />
      <LookupListDialog open={dialog === 'sokho'} onClose={() => setDialog(null)}
        title={label('Dữ liệu sổ kho')} listinfoCode={CODE_SOKHO} condition={{ id_ct: recordId }} />
      <LookupListDialog open={dialog === 'deleted'} onClose={() => setDialog(null)}
        title={label('Dữ liệu đã xoá')} listinfoCode={CODE_DELETED} condition={deletedCondition} />

      <LookupReportDialog open={dialog === 'phaithu'} onClose={() => setDialog(null)}
        title={label('Công nợ phải thu')} reportCode={RPT_PHAI_THU} condition={{ id_ct: recordId }} />
      <LookupReportDialog open={dialog === 'phaitra'} onClose={() => setDialog(null)}
        title={label('Công nợ phải trả')} reportCode={RPT_PHAI_TRA} condition={{ id_ct: recordId }} />

      <UpdateLogDialog open={dialog === 'log'} onClose={() => setDialog(null)} apiCode={apiCode} recordId={recordId} />

      <ChangeCodeDialog open={dialog === 'doima'} onClose={() => setDialog(null)}
        apiCode={apiCode} item={item} formCode={changeCodeForm} onChanged={onChanged} />
    </>
  );
}
